// Moved from `src/hooks/mutation/useMutateUpdateSettings.ts` (now the app
// delegate) with the identity D2 slice (ARCFE-1062). Unblocked by D1
// (ARCFE-1037), which carved `updateUser` into `../api/user`.
//
// Severed per ARCFE-921: both toasts (and the `showToast` option that selects
// between firing and not — a copy concern, so it lives at the delegate) and the
// Rematch `analyticsScribe.saveSettings` dispatch.
//
// FOUR APP COMMANDS CROSS AS PORTS RATHER THAN MOVING, each for a stated
// reason — none of them is a `configureWorkspaceData` injection (delivery
// section 3: the owning side exports the command, app integration sequences it):
//   - `cancelCurrentUserQueries()` and `invalidateCurrentUser()` are app helpers
//     in `src/hooks/query/useQueryUser.ts` that wrap their queryClient call in a
//     try/catch-and-log. `invalidateCurrentUser` is additionally COMPOSITE — it
//     invalidates `GET_USER` *and* the commerce `GET_PRODUCTS` behind that one
//     catch, so reimplementing only the workspace leg here would silently drop a
//     commerce invalidation (the OT4 finding, ARCFE-1033).
//   - `invalidateNotificationPreferences()` targets an UNCARVED app-shell key.
//   - `invalidateCurrentUserLanguage()` lives in `@heidi/runtime/i18n`.
// The `display_language` DATA branch that gates the last one stays here (the
// OT4 rule: data branches in the package, copy-selection at the delegate).
import { logger } from '@heidi/runtime/logger';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_WORKSPACES, type UpdateUserRequest } from '../../contracts';
import { updateUser } from '../api/user';

export const UPDATE_PROFILE = 'UpdateProfile';

/** True across observer remounts while any settings update is in flight. */
export const useIsUpdatingSettings = () =>
  useIsMutating({ mutationKey: [UPDATE_PROFILE] }) > 0;

export interface UseMutateUpdateSettingsOptions {
  /**
   * App-reaction port awaited in `onMutate`, where `cancelCurrentUserQueries()`
   * was awaited. A `GET /user/` already in flight predates this PATCH; if it
   * resolves after the settled refetch it reverts the cache (and any form synced
   * to it) to pre-save values.
   */
  onSettingsMutate?: () => Promise<void> | void;
  /**
   * App-reaction port: fired where the success toast and the Rematch
   * `saveSettings` dispatch fired, with the same variables the dispatch read.
   */
  onSettingsSaved?: (variables: UpdateUserRequest) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onSettingsError?: () => void;
  /**
   * App-reaction port awaited FIRST in `onSettled`, holding the
   * `invalidateCurrentUser()` → `invalidateNotificationPreferences()` pair in
   * that order.
   */
  onSettingsSettled?: () => Promise<void> | void;
  /**
   * App-reaction port awaited LAST in `onSettled`, only when the request
   * carried a `display_language` AND the PATCH succeeded. The refetches it
   * holds assume the backend now agrees with the FE's optimistic locale; on
   * failure they would cache old-language content under the new locale key,
   * and the settled `GET /user/` refetch reverts the optimistic locale instead.
   */
  onDisplayLanguageChanged?: () => Promise<void> | void;
}

/** PATCH /user/ — the user-settings save. */
export function useMutateUpdateSettings(options?: UseMutateUpdateSettingsOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_PROFILE],
    mutationFn: async (data: UpdateUserRequest & { signal?: AbortSignal }) => {
      const { signal, ...userData } = data;
      return await updateUser(userData, signal);
    },
    // A `GET /user/` already in flight predates this PATCH; if it resolves
    // after the settled refetch it reverts the cache (and any form synced to
    // it) to pre-save values. Cancel it so only post-save data can land.
    onMutate: async () => {
      await options?.onSettingsMutate?.();
    },
    onSuccess: (_, variables) => {
      options?.onSettingsSaved?.(variables);
      if (variables.organisation_info !== undefined) {
        void queryClient.invalidateQueries({ queryKey: [GET_WORKSPACES] });
      }
    },
    onError: (error) => {
      logger.error('Error:', error);
      options?.onSettingsError?.();
    },

    onSettled: async (_, error, variables) => {
      await options?.onSettingsSettled?.();

      if (
        error === null &&
        variables.language_time_settings?.display_language !== undefined
      ) {
        await options?.onDisplayLanguageChanged?.();
      }
    },
  });
}
